import React from 'react';
import type { MatNode, MediaType } from '../../../../store/seedanceTypes';
import { useSeedanceUiStore } from '../../../../store/seedanceUiStore';
import VideoAssetCard from '../../../../components/VideoAssetCard';
import './MaterialNodeCard.css';

export const ICONS: Record<MediaType, string> = { image: '🖼️', video: '🎬', audio: '🎵' };
export const LABELS: Record<MediaType, string> = { image: '图片槽', video: '视频槽', audio: '音频槽' };

const renderNodePreview = (
  n: MatNode,
  onPreviewImageError?: (nodeId: string) => void,
  onPlayAudio?: (node: MatNode) => void,
) => {
  if ((n.url || n.thumb) && n.type === 'image')
    return (
      <img
        src={n.thumb || n.url}
        alt=""
        onError={() => onPreviewImageError?.(n.id)}
      />
    );
  if ((n.url || n.thumb) && n.type === 'video') {
    return <VideoAssetCard videoUrl={n.url || null} poster={n.thumb || null} variant="mini" />;
  }
  if (n.type === 'audio' && n.url) {
    return (
      <>
        <div className="audio-wave">
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="bar" key={i} />
          ))}
        </div>
        <div className="mat-overlay">
          <button
            type="button"
            className="prev-btn"
            onClick={(e) => {
              e.stopPropagation();
              onPlayAudio?.(n);
            }}
          >
            播放
          </button>
        </div>
      </>
    );
  }
  return <div className="mat-placeholder">{ICONS[n.type]}</div>;
};

interface MaterialNodeCardProps {
  node: MatNode;
  draggingId: string | null;
  dragOverId: string | null;
  onDragStart: (id: string) => (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (id: string) => (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (targetId: string) => (e: React.DragEvent) => void;
  onRemove: (id: string) => void;
  onUpdateName: (id: string, val: string) => void;
  onFinalizeName: (id: string) => void;
  onToggleConnect: (id: string) => void;
  onOpenLibPicker: (id: string) => void;
  onPreview: (asset: { url: string; type: 'image' | 'video' }) => void;
  /** OSS/CDN 直链过期时触发，按素材 ID 重新拉取可用地址 */
  onPreviewImageError?: (nodeId: string) => void;
}

export const MaterialNodeCard: React.FC<MaterialNodeCardProps> = ({
  node,
  draggingId,
  dragOverId,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onRemove,
  onUpdateName,
  onFinalizeName,
  onToggleConnect,
  onOpenLibPicker,
  onPreview,
  onPreviewImageError,
}) => {
  const showToast = useSeedanceUiStore((s) => s.showToast);
  const setModalAudioUrl = useSeedanceUiStore((s) => s.setModalAudioUrl);
  const setModalAudioTitle = useSeedanceUiStore((s) => s.setModalAudioTitle);

  const playAudioPreview = (target: MatNode) => {
    const url = (target.url || '').trim();
    if (!url) {
      showToast('暂无音频', 'e');
      return;
    }
    setModalAudioTitle(target.name);
    setModalAudioUrl(url);
  };

  const extraClass = [
    node.connected ? 'connected' : '',
    draggingId === node.id ? 'dragging' : '',
    dragOverId === node.id ? 'drag-over' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`node mat-node ${extraClass}`}
      draggable
      onDragStart={onDragStart(node.id)}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver(node.id)}
      onDragLeave={onDragLeave}
      onDrop={onDrop(node.id)}
    >
      <div className="node-head">
        <div className="node-ico">{ICONS[node.type]}</div>
        <span className="node-title">{LABELS[node.type]}</span>
        <button
          type="button"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onRemove(node.id);
          }}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            cursor: 'pointer',
            fontSize: 16,
          }}
        >
          ✕
        </button>
      </div>
      <div className="node-body">
        <div
          className="mat-preview"
          onClick={() => {
            const url = (node.url || '').trim();
            if ((node.type === 'image' || node.type === 'video') && url) {
              onPreview({ url, type: node.type });
            } else if (node.type === 'audio') {
              if (url) playAudioPreview(node);
              else onOpenLibPicker(node.id);
            } else {
              onOpenLibPicker(node.id);
            }
          }}
        >
          {renderNodePreview(node, onPreviewImageError, playAudioPreview)}
        </div>
        <input
          className="mat-name"
          value={node.name}
          onChange={(e) => onUpdateName(node.id, e.target.value)}
          onBlur={() => onFinalizeName(node.id)}
        />
        <div className="mat-btns">
          {(node.type === 'image' || node.type === 'video' || node.type === 'audio') && (
            <button type="button" className="mat-btn" onClick={() => onOpenLibPicker(node.id)}>
              📦 素材库
            </button>
          )}
          <button
            type="button"
            className={`mat-btn connect${node.connected ? ' active' : ''}`}
            onClick={() => onToggleConnect(node.id)}
          >
            {node.connected ? '✓ 已激活' : '激活此模块'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaterialNodeCard;
